import { Permission } from '@aspectus/permissions'
import { Pinia } from 'pinia'
import { isRef, unref } from 'vue'
import { curry, path } from 'ramda'
import { Or, And } from '@aspectus/permissions'
import { accessConfig } from '@/packages/vue-router-permissions'
import Route403 from '@m/cabinet/views/Route403.vue'
import { ACCESS_TOKENS } from './accessTokens'

import { ProfileTypeChoices } from '@m/cabinet/modules/Profile/const'

interface UserInfo {
  info: {
    userType: string,
    profileType: {
      label: string,
      value: string
    }
  }
  isAuthenticated: () => boolean
}

interface User extends UserInfo {
  determined: boolean,
}

type GetterResult = { user: User }
export function waitForUser(
  state: unknown,
  getter: (state: unknown) => GetterResult,
  timeout = 5000,
  tick = 3
): Promise<User> {
  return new Promise((resolve, reject) => {
    const maxAttempts = tick
    let attempts = 0

    const interval = setInterval(() => {
      let userState = getter(state)
      if (userState) {
        let { user } = userState
  
        if (!user) {
          clearInterval(interval)
          reject()
          return
        }
  
        if (isRef(user)) {
          user = unref(user) as User
        }
  
        if (user.determined) {
          clearInterval(interval)
          if (user.isAuthenticated()) {
            resolve(user)
          } else {
            reject()
          }
          return
        }

      }

      attempts++
      if (attempts >= maxAttempts) {
        clearInterval(interval)
        reject()
      }
    }, tick)

    // setTimeout(() => {
    //   clearInterval(interval)
    // }, timeout)
  })
}
export class UserCheckerPermissionBase extends Permission {
  getter: (state: unknown) => GetterResult
  timeout: number
  tick: number
  constructor({
    getter,
    timeout = 5000,
    tick = 10,
  }: { getter?: (state: unknown) => GetterResult; timeout?: number; tick?: number } = {}) {
    super()
    this.getter = getter || (() => ({ user: { determined: false, isAuthenticated: () => false, info: { userType: '' } } as User }))
    this.timeout = timeout
    this.tick = tick
  }

  getStore(vm: { pinia: Pinia }, { pinia }: { pinia?: Pinia } = {}): Pinia {
    return (vm && vm.pinia) || pinia
  }

  waitForUser(
    vm: { pinia: {} },
    args?: [{ pinia?: Pinia }]
  ): Promise<User> {
    const store = vm?.pinia
    return waitForUser(store, this.getter, this.timeout, this.tick)
  }

  checkUser(...arg: unknown[]): boolean {
    return false
  }
  onHasAccess(
    vm: { pinia: Pinia },
    ...args
  ): Promise<void> {
    return this.waitForUser(vm, ...args).then((user) => {
      if (!this.checkUser(user, vm, ...args)) {
        throw new Error('Access denied')
      }
    })
  }
}

export class UserCheckerPermission extends UserCheckerPermissionBase {
  checker: (...arg: unknown[]) => boolean
  constructor({
    checker,
    ...rest
  }: {
    checker: (...arg: unknown[]) => boolean
    [key: string]: unknown
  }) {
    super(rest)
    this.checker = checker
  }

  checkUser(...arg: unknown[]): boolean {
    return this.checker(...arg)
  }
  onHasAccess(
    vm: { pinia: Pinia },
    params
  ): any {
    return new Promise<any>((resolve, reject) => {
      this.waitForUser(params, params).then((user) => {
        if (!this.checkUser(user, vm, params)) {
          // throw user;
          reject('user')
        }
        resolve(user)
      }, err => reject(err))
    })
    return this.waitForUser(params, params).then((user) => {
      if (!this.checkUser(user, vm, ...params)) {
        throw user;
      }
    })
  }
}

export function checkAuth(
  checker: (...arg: unknown[]) => boolean,
  config = {},
  Base = UserCheckerPermission
): UserCheckerPermission {
  return new Base(Object.assign({ checker, getter: path(['state', 'value', 'authStore']) },  config ), )
}

class SimplePermission extends Permission {
  checker: (...args: unknown[]) => boolean
  constructor({
    checker,
    ...rest
  }: {
    checker: (...args: unknown[]) => boolean
    [key: string]: unknown
  }) {
    super(rest)
    this.checker = checker
  }

  onHasAccess(...args: unknown[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.checker(...args)) {
        resolve()
      } else { 
        reject()
      }
    })
  }
}

export function simpleChecker<T>(
  checker: (arg: T) => boolean,
  Base = SimplePermission
): SimplePermission {
  return new Base({ checker })
}

// Usage example
const simpleCheckPermission = (checker: (user: User) => boolean): SimplePermission => simpleChecker(checker)
const userCheckPermission = (checker: (user: User) => boolean) : UserCheckerPermission => checkAuth(checker)

// Export `isType` function as it is part of the original code but not provided here.
export const isType = (type: string, user: { info: { userType: string } }): boolean => user.info.userType === type;

export const isPermittedSimple = (permissions: string[]): boolean =>
  !!(
    ACCESS_TOKENS &&
    permissions.every((permission) => ACCESS_TOKENS.includes(permission))
  )

export const isPermitted = curry(
  (permissions: string[]) =>
    !!(
      ACCESS_TOKENS &&
      permissions.every((permission) => ACCESS_TOKENS.includes(permission))
    )
)
export const hasProfileType = curry(
  (permissions: string[], user: User): boolean => !!(
      user.info?.profileType
  )
)
export const canViewMedicalFacility = curry(
  (permissions: string[], user: User): boolean => user.info?.profileType && !!(
      user.info?.profileType.value != ProfileTypeChoices.B1 
  )
)
export const canViewMedicalFacilityDocumentFlow = curry(
  (permissions: string[], user: User): boolean => user.info?.profileType && !!(
      user.info?.profileType.value == ProfileTypeChoices.B2 ||user.info?.profileType.value == ProfileTypeChoices.B3 
  )
)
export const canViewDoctor = curry(
  (permissions: string[], user: User): boolean => user.info?.profileType && !!(
      user.info?.profileType.value == ProfileTypeChoices.B1 ||
      user.info?.profileType.value == ProfileTypeChoices.B2
  )
)
export const isDoctor = curry(
  (permissions: string[], user: User): boolean => user.info?.profileType && !!(
      user.info?.profileType.value == ProfileTypeChoices.B1
  )
)
  

export const toBeNotAuthenticated = simpleCheckPermission(
  (user: UserInfo): boolean => !user.isAuthenticated()
)

export const toHasOwnPermission = (permissions: string[]): SimplePermission =>
  simpleCheckPermission(() => isPermitted(permissions))

export const toHasProfileType = (permissions: string[]): UserCheckerPermission =>
  userCheckPermission((user: User): boolean => hasProfileType(permissions)(user))

export const toHasFacilityPermission = (permissions: string[]): UserCheckerPermission =>
  userCheckPermission((user: User): boolean => canViewMedicalFacility(permissions)(user))

export const toHasFacilityDocumentFlowPermission = (permissions: string[]): UserCheckerPermission =>
  userCheckPermission((user: User): boolean => canViewMedicalFacilityDocumentFlow(permissions)(user))

export const toHasDoctorPermission = (permissions: string[]): UserCheckerPermission =>
  userCheckPermission((user: User): boolean => canViewDoctor(permissions)(user))


export const toHasPermission = (permissions: string[]): InstanceType<typeof Or> => {
  return new Or(toHasOwnPermission(permissions))
}
export const toHasPermissionWithType = (permissions: string[]): InstanceType<typeof And> => {
  return new And(toHasProfileType(permissions) )
}
export const toHasPermissionToFacility = (permissions: string[]): InstanceType<typeof And> => {
  return new And(toHasProfileType(permissions), toHasFacilityPermission(permissions) )
}
export const toHasPermissionToFacilityDocumentFlow = (permissions: string[]): InstanceType<typeof And> => {
  return new And(toHasProfileType(permissions), toHasFacilityDocumentFlowPermission(permissions) )
}
export const toHasPermissionToDoctor = (permissions: string[]): InstanceType<typeof And> => {
  return new And(toHasProfileType(permissions), toHasDoctorPermission(permissions) )
}

export const accessPermission = (
  permissions: string[],
  options: { [key: string]: unknown },
  rejectRoute = { 
    name: 'cabinet:no-access'
  }
): { [key: string]: unknown } => {
  return {
    accessConfig: accessConfig.apply(this, [
      toHasPermission(permissions),
      rejectRoute,
      options,
    ]),
  }
}
export const accessTypePermission = (
  permissions: string[],
  options: { [key: string]: unknown }
): { [key: string]: unknown } => {
  return {
    accessConfig: accessConfig.apply(this, [
      toHasPermissionWithType(permissions),
      { 
        name: 'cabinet:no-access'
      },
      options,
    ]),
  }
}
export const facilityAccessPermission = (
  permissions: string[],
  options: { [key: string]: unknown }
): { [key: string]: unknown } => {
  return {
    accessConfig: accessConfig.apply(this, [
      toHasFacilityPermission(permissions),
      { 
        name: 'cabinet:no-access'
      },
      options,
    ]),
  }
}

export const facilityDocumentFlowAccessPermission = (
  permissions: string[],
  options: { [key: string]: unknown }
): { [key: string]: unknown } => {
  return {
    accessConfig: accessConfig.apply(this, [
      toHasPermissionToFacilityDocumentFlow(permissions),
      { 
        name: 'cabinet:no-access'
      },
      options,
    ]),
  }
}

export const doctorAccessPermission = (
  permissions: string[],
  options: { [key: string]: unknown }
): { [key: string]: unknown } => {
  return {
    accessConfig: accessConfig.apply(this, [
      toHasPermissionToDoctor(permissions),
      { 
        name: 'cabinet:no-access'
      },
      options,
    ]),
  }
}
